import { getChatImageBlob } from "@ando/core";
import type { ChatImageAttachment } from "@ando/core";
import { Dialog, DialogContent, DialogTitle } from "@ando/ui";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
/**
 * ChatImageThumbnails — render user-uploaded chat images under their
 * own message bubble.
 *
 * Background (Jun 23, 2026): the chat composer's image-attach surface
 * uploads files to MinIO via ``POST /uploads/chat-image`` and persists
 * the attachment refs as ``messages.image_attachments`` JSONB. The BE
 * forwards them to the VLM (Gemini) so the agent can "see" the image
 * and answer ``Cosa vedi?`` correctly — but **the FE never rendered
 * the image back in the chat history**. Effect: users saw their own
 * "Cosa vedi?" prompt with no visual context attached, even though
 * the agent's reply clearly described the screenshot. This component
 * closes the gap.
 *
 * Why a dedicated component (vs inline JSX in ``Messages.tsx``):
 *   * Each thumbnail needs an auth-bearing blob fetch (the
 *     ``/uploads/chat-image`` endpoint is tenant-isolated — a naked
 *     ``<img src=...>`` would 401). Encapsulating the blob lifecycle
 *     keeps the parent presentational.
 *   * The blob-URL cleanup (``URL.revokeObjectURL`` on unmount) is
 *     easy to forget; isolating it per-image avoids parent-level
 *     bookkeeping.
 *
 * Pattern mirrors ``useInlineImageMap`` in
 * ``features/sources/components/EmailThreadMessages.tsx`` — the
 * canonical email-inline-image fetch loop. Same blob-URL lifecycle,
 * same fail-soft on transient errors.
 */
import { type FC, useEffect, useState } from "react";

interface ChatImageThumbnailsProps {
  readonly attachments: ReadonlyArray<ChatImageAttachment>;
}

export const ChatImageThumbnail: FC<{ attachment: ChatImageAttachment }> = ({ attachment }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        const blob = await getChatImageBlob(attachment.minio_key);
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        createdUrl = url;
        setBlobUrl(url);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.minio_key]);

  if (errored) {
    return (
      <div
        className="flex h-32 w-32 items-center justify-center rounded-md border border-hairline bg-cream/40 text-[10px] text-ink-3"
        title={attachment.filename}
      >
        ⚠ Immagine non disponibile
      </div>
    );
  }

  if (!blobUrl) {
    // Skeleton while the blob is in flight — same footprint as the
    // final thumbnail so layout doesn't jump on load.
    return (
      <div
        className="h-32 w-32 animate-pulse rounded-md border border-hairline bg-cream/60"
        aria-label={`Caricamento ${attachment.filename}…`}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Thumbnail acts as the dialog trigger. We use a plain ``<button>``
          (not ``<DialogTrigger asChild>``) because the surrounding row is
          inside the chat message stream — opening the lightbox should NOT
          bubble click handlers up to the message toolbar / row click
          surface. ``type="button"`` prevents accidental form submission
          if the composer is ever wrapped in a ``<form>``. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="block overflow-hidden rounded-md border border-hairline transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange"
        title={attachment.filename}
        aria-label={`Apri ${attachment.filename} a schermo intero`}
      >
        <img
          src={blobUrl}
          alt={attachment.filename}
          className="block h-32 max-w-[256px] object-cover"
        />
      </button>
      {/* Full-size lightbox. We override the primitive's ``max-w-lg``
          default with ``max-w-[90vw]`` + ``max-h-[90vh]`` so the
          image renders at its natural ratio without crowding the
          screen. The Radix ``DialogTitle`` lives behind
          ``VisuallyHidden`` because we don't need a visible header
          — the image itself is the content — but Radix requires a
          title for screen-reader semantics. */}
      <DialogContent
        ariaTitle={attachment.filename}
        className="grid max-h-[90vh] w-[min(90vw,1280px)] max-w-[90vw] place-items-center gap-0 border-0 bg-transparent p-0 shadow-none"
      >
        <VisuallyHidden.Root>
          <DialogTitle>{attachment.filename}</DialogTitle>
        </VisuallyHidden.Root>
        <img
          src={blobUrl}
          alt={attachment.filename}
          className="max-h-[90vh] max-w-full rounded-md object-contain shadow-2xl"
        />
      </DialogContent>
    </Dialog>
  );
};

export const ChatImageThumbnails: FC<ChatImageThumbnailsProps> = ({ attachments }) => {
  if (!attachments.length) return null;
  return (
    <div className="-mt-2 flex justify-end gap-2">
      {attachments.map((att) => (
        <ChatImageThumbnail key={att.minio_key} attachment={att} />
      ))}
    </div>
  );
};
ChatImageThumbnails.displayName = "ChatImageThumbnails";
